import { Check } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/src/utils/styles'
import { Close } from '../ui/icons/close'
import { StepRemainingTime } from './step-remaining-time'

export enum LoadingType {
  Progress = 'progress',
  Spinner = 'spinner',
  Pulse = 'pulse',
}

interface StepCircleProps extends ComponentProps<'div'> {
  isActive?: boolean
  isSuccess?: boolean
  isError?: boolean
  isLoading?: boolean
  hideIcon?: boolean
  className?: string
  icon?: ReactNode
  type?: LoadingType
  transferDetails?: {
    startedAtMs: number
    estimatedTimeMs: number
  }
  svgContainerClassName?: string
}

export const StepCircle = ({
  isActive,
  isSuccess,
  isError,
  isLoading,
  hideIcon,
  className,
  icon,
  type = LoadingType.Spinner,
  transferDetails,
  svgContainerClassName,
  ref,
}: StepCircleProps) => {
  if (
    type === LoadingType.Progress &&
    transferDetails &&
    !isSuccess &&
    !isError
  ) {
    return (
      <StepRemainingTime
        startedAtMs={transferDetails.startedAtMs}
        estimatedTimeMs={transferDetails.estimatedTimeMs}
        svgContainerClassName={svgContainerClassName}
      />
    )
  }

  return (
    <div
      ref={ref}
      className={cn(
        'w-6 h-6 rounded-full border-2 border-border2 relative flex items-center justify-center',
        isActive && 'border-accent-primary',
        isSuccess && 'border-success-text',
        isError && !isLoading && 'border-danger-text',
        {
          // Use accent-primary for loading spinner when not error, danger-text when error+loading
          'after:rounded-full after:border-2 after:absolute after:w-6 after:h-6 after:border-l-transparent':
            isLoading,
          'after:border-b-accent-primary after:border-r-accent-primary after:border-t-accent-primary':
            isLoading && !isError,
          'after:border-b-danger-text after:border-r-danger-text after:border-t-danger-text':
            isLoading && isError,
          'after:animate-[spin_0.7s_linear_infinite]':
            type === LoadingType.Spinner,
          'after:animate-ping after:border-accent-primary border-accent-primary':
            type === LoadingType.Pulse,
        },
        className,
      )}
    >
      {icon ?? (
        <>
          {isSuccess && !hideIcon && (
            <Check className='absolute w-4 h-4 text-success-text' />
          )}
          {isError && !isLoading && !hideIcon && (
            <Close className='absolute w-4 h-4 text-danger-text' />
          )}
        </>
      )}
    </div>
  )
}

StepCircle.displayName = 'StepCircle'
